import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MAX_PHOTOS = 5;

interface ProtocolEvidenceFieldsProps {
  notes: string;
  onNotesChange: (value: string) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  hidePhotos?: boolean;
  notesLabel?: string;
  notesPlaceholder?: string;
}

export function ProtocolEvidenceFields({
  notes,
  onNotesChange,
  files,
  onFilesChange,
  hidePhotos = false,
  notesLabel = "Stan przedmiotu (opcjonalnie)",
  notesPlaceholder = "Np. rysy na obudowie, pełny bak...",
}: ProtocolEvidenceFieldsProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const addFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;
    onFilesChange([...files, ...incoming].slice(0, MAX_PHOTOS));
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="equipment-condition">{notesLabel}</Label>
        <Textarea
          id="equipment-condition"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={notesPlaceholder}
          maxLength={2000}
        />
      </div>
      {!hidePhotos && (
        <div className="space-y-2">
          <Label>Zdjęcia (opcjonalnie)</Label>
          <input
            ref={inputRef}
            id="equipment-photos"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            className="hidden"
            onChange={(e) => addFiles(Array.from(e.target.files ?? []))}
          />
          {files.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${file.lastModified}-${index}`}
                    className="group relative aspect-square overflow-hidden rounded-md border bg-muted"
                  >
                    {previewUrls[index] ? (
                      <button
                        type="button"
                        className="h-full w-full"
                        onClick={() => setPreview(previewUrls[index])}
                      >
                        <img src={previewUrls[index]} alt={file.name} className="h-full w-full object-cover" />
                      </button>
                    ) : (
                      <p className="flex h-full items-center justify-center p-2 text-center text-xs">{file.name}</p>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute right-1 top-1 h-6 w-6 rounded-full"
                      onClick={() => removeFile(index)}
                      aria-label="Usuń zdjęcie"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              {files.length < MAX_PHOTOS && (
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => inputRef.current?.click()}>
                  <Camera className="mr-2 h-4 w-4" />
                  Dodaj kolejne zdjęcie
                </Button>
              )}
            </div>
          ) : (
            <Button type="button" variant="outline" className="w-full" onClick={() => inputRef.current?.click()}>
              <Camera className="mr-2 h-4 w-4" />
              Dodaj zdjęcie
            </Button>
          )}
          <p className="text-xs text-muted-foreground">Do 5 zdjęć. JPG, PNG, WEBP lub HEIC. Kliknij miniaturę, aby powiększyć.</p>
        </div>
      )}
      {preview && (
        <button
          type="button"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreview(null)}
          aria-label="Zamknij podgląd"
        >
          <img
            src={preview}
            alt="Podgląd zdjęcia"
            className="max-h-[85vh] max-w-full rounded-md object-contain"
            onClick={(event) => event.stopPropagation()}
          />
          <span className="absolute right-4 top-4 rounded-full bg-background/90 p-2">
            <X className="h-4 w-4" />
          </span>
        </button>
      )}
    </div>
  );
}
